import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { Group, ScrollArea, Stack, Text, Title } from '@mantine/core'
import { Modal as MantineModal } from '@mantine/core'
import { Alert, Button } from '@/components/Elements'
import { TextAreaController } from '@/components/FormElements'
import { useAppState } from '@/features/app/hooks'
import { bookingsAPI } from '@/features/bookings/api'
import { convertListNamesToNamesNoPrefix } from '@/helpers/common/utils'
import { FormTypes } from '@/types'
import { ValidationsUtils } from '@/utils'
import useStyles from './BookingReportModal.styles'

interface IProps {
  visible: boolean
  bookingReportId: number | undefined
  bookingReportClient: string
  bookingReportChildren: string[]
  onLoginModalClose: () => void
  onSaveReport?: () => void
}

export type FormValues = {
  activities: string
  interactions: string
  routine: string
  suggestions: string
}

export const BookingReportModal = ({
  visible,
  bookingReportId,
  bookingReportClient,
  bookingReportChildren,
  onLoginModalClose,
  onSaveReport,
}: IProps) => {
  const { t } = useTranslation()
  const { classes } = useStyles()
  const [submitted, setSubmitted] = useState<boolean | null>(false)
  const [alertError, setAlertError] = useState<FormTypes.ValidationErrors | null>(null)
  const {
    appState: { mobileView },
  } = useAppState()

  useEffect(() => {
    setSubmitted(false)
    reset({
      activities: '',
      interactions: '',
      routine: '',
      suggestions: '',
    })
  }, [])

  const handleLater = async () => {
    setAlertError(null)
    try {
      await bookingsAPI.showBookingReportLater(bookingReportId || 0)
      onLoginModalClose()
    } catch (err) {
      const serverError = err as FormTypes.ValidationErrors
      setAlertError(serverError)
      ValidationsUtils.setServerSideErrors(serverError?.errors, setError)
    }
  }
  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    reset,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      activities: '',
      interactions: '',
      routine: '',
      suggestions: '',
    },
    resolver: yupResolver(
      Yup.object().shape({
        activities: Yup.string().required('field.error.required').min(10, 'field.error.min_length'),
        interactions: Yup.string()
          .required('field.error.required')
          .min(10, 'field.error.min_length'),
        routine: Yup.string().required('field.error.required').min(10, 'field.error.min_length'),
        suggestions: Yup.string()
          .required('field.error.required')
          .min(10, 'field.error.min_length'),
      })
    ),
  })

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setAlertError(null)
    setSubmitted(false)
    try {
      const values = {
        ...data,
        id: bookingReportId || 0,
      }
      await bookingsAPI.saveBookingReport(values)
      onSaveReport && onSaveReport()
      setSubmitted(true)
      onLoginModalClose()
    } catch (err) {
      const serverError = err as FormTypes.ValidationErrors
      setAlertError(serverError)
      ValidationsUtils.setServerSideErrors(serverError?.errors, setError)
    }
  }

  return (
    <>
      <MantineModal onClose={onLoginModalClose} opened={visible} withCloseButton={false} centered>
        <div className={classes.wrap}>
          <div className={'w-100 d-flex justify-center'}>
            <Title order={2}>{t('report.title')}</Title>
          </div>
          {!submitted && (
            <>
              <div className={'w-100 d-flex mt-2'}>
                <Text size={'xs'}>{t('report.subtitle')}</Text>
              </div>
              <div className={'w-100 d-flex mt-2'}>
                <Text size={'xs'}>{t('parent') + ': ' + bookingReportClient}</Text>
              </div>
              <div className={'w-100 d-flex'}>
                <Text size={'xs'}>
                  {t('children') + ': ' + convertListNamesToNamesNoPrefix(bookingReportChildren)}
                </Text>
              </div>
            </>
          )}
          {alertError && (
            <Alert type={'error'} mb={'sm'} mt={'sm'}>
              {alertError?.message || t('error')}
            </Alert>
          )}
          {submitted && !alertError && (
            <div className={'w-100 d-flex justify-center mt-4'}>
              <Text>{t('report.success')}</Text>
            </div>
          )}
          {!submitted && (
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack className={'mt-4'}>
                <ScrollArea className={classes.scrollArea}>
                  <Title order={5}>{t('report.activities.title')}</Title>
                  <TextAreaController
                    control={control}
                    name={'activities'}
                    id={'activities'}
                    size={'md'}
                    label={t('report.activities.question')}
                    placeholder={t('report.activities.placeholder')}
                    mb={'md'}
                    maxRows={4}
                    minRows={2}
                    translateParams={{ count: 10 }}
                  />
                  <Title order={5}>{t('report.interaction.title')}</Title>
                  <TextAreaController
                    control={control}
                    name={'interactions'}
                    id={'interactions'}
                    size={'md'}
                    label={t('report.interaction.question')}
                    placeholder={t('report.interaction.placeholder')}
                    mb={'md'}
                    maxRows={4}
                    minRows={2}
                    translateParams={{ count: 10 }}
                  />
                  <Title order={5}>{t('report.routine.title')}</Title>
                  <TextAreaController
                    control={control}
                    name={'routine'}
                    id={'routine'}
                    size={'md'}
                    label={t('report.routine.question')}
                    placeholder={t('report.routine.placeholder')}
                    mb={'md'}
                    maxRows={4}
                    minRows={2}
                    translateParams={{ count: 10 }}
                  />
                  <Title order={5}>{t('report.suggestions.title')}</Title>
                  <TextAreaController
                    control={control}
                    name={'suggestions'}
                    id={'suggestions'}
                    size={'md'}
                    label={t('report.suggestions.question')}
                    placeholder={t('report.suggestions.placeholder')}
                    mb={'md'}
                    maxRows={4}
                    minRows={2}
                    translateParams={{ count: 10 }}
                  />
                </ScrollArea>

                <Group position="right">
                  <Button
                    disabled={isSubmitting}
                    variant="default"
                    color="dark"
                    onClick={handleLater}
                  >
                    {t('report.later')}
                  </Button>
                  <Button
                    color={mobileView ? 'primary' : 'secondary'}
                    type={'submit'}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                  >
                    {t('report.submit')}
                  </Button>
                </Group>
              </Stack>
            </form>
          )}
        </div>
      </MantineModal>
    </>
  )
}
